/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { MissingAdSetting } from '@/shared/utils/ad-readiness';
import { clampAdBudgetReais } from '@/shared/utils/ad-dialog-helpers';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Text, UnstyledButton } from '@/shared/ui';
import { EDITABLE_PORTALS, RECREATE_ON_EDIT_PORTALS, type PublicationRow } from './types';
import { FF_APP_POST_IMAGE_EDIT } from '@/shared/utils/feature-flag';
import { isInstagramEditHidden } from '@/shared/utils/instagram-edit-policy';
import {
  EditActions,
  EditImageGrid,
  EditLoading,
  EditText,
  ImageRemoveConfirm,
  type TranslateFn,
} from './publication-row-parts';
import {
  BoostPanel,
  DisconnectedWarning,
  QueuedProcessingHint,
  resolveCanBoost,
  RowActions,
  RowMeta,
} from './publication-row-header';

/**
 * Builds the public Facebook post URL for a publication.
 */
function getFacebookUrl(row: PublicationRow): string | null {
  const parts = row.post_id ? row.post_id.split('_') : [];
  if (parts.length >= 2) {
    return `https://www.facebook.com/${parts[0]}/posts/${parts.slice(1).join('_')}`;
  }
  return row.listing_id ? `https://www.facebook.com/marketplace/item/${row.listing_id}` : null;
}

/**
 * Builds the public Instagram post URL for a publication.
 */
function getInstagramUrl(row: PublicationRow): string | null {
  return row.permalink ?? null;
}

/**
 * Builds the public OLX listing URL for a publication.
 *
 * Prefers the ad URL OLX itself returned after moderation (``permalink``);
 * the ``list_id`` form is only a fallback for entries saved before that.
 */
function getOlxUrl(row: PublicationRow): string | null {
  if (row.permalink) return row.permalink;
  // OLX's own short ad address (the form its API returns); ``/anuncio/{id}`` does not exist.
  return row.listing_id ? `https://www.olx.com.br/vi/${row.listing_id}.htm` : null;
}

const POST_URL_RESOLVERS: Record<string, (row: PublicationRow) => string | null> = {
  facebook: getFacebookUrl,
  instagram: getInstagramUrl,
  olx: getOlxUrl,
};

/**
 * Resolves the external post URL for a publication row.
 */
function getRowPostUrl(row: PublicationRow): string | null {
  const resolver = POST_URL_RESOLVERS[row.portal];
  return resolver ? resolver(row) : null;
}

interface RowProps {
  row: PublicationRow;
  t: TranslateFn;
  connectedPortals: string[];
  removing: string | null;
  updating: string | null;
  editingRow: string | null;
  editText: string;
  setEditText: (v: string) => void;
  loadingEditContent: boolean;
  editImages: string[];
  editImagesOriginal: string[];
  setEditImages: (imgs: string[]) => void;
  getStatusLabel: (status: string) => string;
  onEdit: (row: PublicationRow) => void;
  onConfirmRemove: (row: PublicationRow) => void;
  onEditSave: (row: PublicationRow) => void;
  onCancelEdit: () => void;
  compact?: boolean;
  boostingRow?: string | null;
  onBoost?: (row: PublicationRow, budget: number, duration: number) => void;
  /** The agency's saved daily budget (cents) and duration (days); 0 when it has saved none. */
  defaultBoostBudget?: number;
  defaultBoostDuration?: number;
  /** What the agency must still fill in before it can boost this row's platform. */
  boostMissing?: MissingAdSetting[];
  /** Where the agency fills it in. */
  adSettingsHref?: string;
  fbHasPaymentMethod?: boolean;
  /** ISO-8601 expiry timestamp for this row's own network connection token, if known. */
  tokenExpiresAt?: string | null;
  onRetry?: (row: PublicationRow) => void;
  retrying?: string | null;
  /** Opens the explanation of a Lead Ad that was requested but not created. */
  onShowAdError?: (row: PublicationRow) => void;
  /** The agency's ad account, so a running Lead Ad can link to Ads Manager. */
  adAccountId?: string | null;
  /**
   * Opens the property's edit form. An OLX ad is built from the property
   * itself, so its "Editar" edits the property (saving re-sends the ad);
   * without a form to open, OLX shows no "Editar".
   */
  onEditProperty?: () => void;
}

/**
 * Message shown when a non-Facebook portal has no selected images.
 */
function NoImagesMessage({ t }: { t: TranslateFn }) {
  return (
    <Text as="p" className="text-[length:var(--text-micro)] text-[var(--status-error-text)]">
      {t('publications.noImagesSelected')}
    </Text>
  );
}

/**
 * Provides state and handlers for removing row images.
 */
function useImageRemoval(row: PublicationRow, editImages: string[], setEditImages: (imgs: string[]) => void) {
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);
  const isRecreatePortal = RECREATE_ON_EDIT_PORTALS.has(row.portal);
  const request = (index: number) => {
    if (isRecreatePortal) setPendingIndex(index);
    else setEditImages(editImages.filter((_, i) => i !== index));
  };
  const confirm = () => {
    if (pendingIndex === null) return;
    setEditImages(editImages.filter((_, i) => i !== pendingIndex));
    setPendingIndex(null);
  };
  return { pendingIndex, setPendingIndex, isRecreatePortal, request, confirm };
}

/**
 * Renders the inline edit panel for a publication row.
 */
function EditPanel(props: RowProps) {
  const { row, t, editText, setEditText, loadingEditContent, editImages, setEditImages, updating, onEditSave, onCancelEdit } = props;
  const { pendingIndex, setPendingIndex, isRecreatePortal, request, confirm } = useImageRemoval(row, editImages, setEditImages);

  if (loadingEditContent) return <EditLoading t={t} />;

  const isFacebook = row.portal === 'facebook';
  const isLastImage = !isFacebook && editImages.length === 1;
  const noImages = FF_APP_POST_IMAGE_EDIT && editImages.length === 0 && !isFacebook;
  const saveDisabled = updating === row.key || noImages;

  return (
    <div className="space-y-(--space-3) pt-(--space-1)">
      {FF_APP_POST_IMAGE_EDIT && editImages.length > 0 && (
        <EditImageGrid images={editImages} pending={pendingIndex} isLastImage={isLastImage} onRemove={request} t={t} />
      )}
      {noImages && <NoImagesMessage t={t} />}
      {FF_APP_POST_IMAGE_EDIT && isRecreatePortal && pendingIndex !== null && (
        <ImageRemoveConfirm onCancel={() => setPendingIndex(null)} onConfirm={confirm} t={t} />
      )}
      <EditText value={editText} rowKey={row.key} onChange={setEditText} t={t} />
      <EditActions rowKey={row.key} isSaving={updating === row.key} saveDisabled={saveDisabled} onSave={() => onEditSave(row)} onCancel={onCancelEdit} t={t} />
    </div>
  );
}

/**
 * Centered dialog around the post editor, so editing gets the same focused
 * treatment as boosting instead of expanding the row. Closed only through its
 * own buttons or Escape -- not by clicking the backdrop, which would throw
 * away text the agency was typing.
 */
function EditModal(props: RowProps) {
  const { row, t, onCancelEdit } = props;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancelEdit(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancelEdit]);

  return createPortal(
    <div className="fixed inset-0 z-[1100] flex items-center justify-center">
      <div className="absolute inset-0 bg-[var(--surface-inverse)]/40" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('publications.editPublication')}
        className="relative w-full max-w-lg mx-(--space-4) max-h-[85vh] flex flex-col rounded-(--radius-xl) bg-(--surface-primary) border border-(--border-subtle) shadow-xl overflow-hidden"
        data-testid={`edit-modal-${row.key}`}
      >
        <div className="flex items-center justify-between px-(--space-5) py-(--space-4) border-b border-(--border-subtle)">
          <Text className="m-0 text-sm font-semibold text-(--text-primary)">
            {t('publications.editPublication')}
          </Text>
          <UnstyledButton
            onClick={onCancelEdit}
            aria-label={t('publications.actions.cancel')}
            data-testid={`edit-modal-close-${row.key}`}
            className="w-6 h-6 flex items-center justify-center rounded-(--radius-md) border-none bg-transparent hover:bg-(--surface-secondary) cursor-pointer text-(--text-tertiary) text-lg"
          >
            &times;
          </UnstyledButton>
        </div>
        <div className="px-(--space-5) py-(--space-4) overflow-y-auto">
          <EditPanel {...props} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Local boost panel state for a single publication row.
 */
function useBoostPanelState(defaultBoostBudget: number, defaultBoostDuration: number) {
  const [showBoostPanel, setShowBoostPanel] = useState(false);
  const [boostBudget, setBoostBudget] = useState(clampAdBudgetReais(Math.round(defaultBoostBudget / 100)));
  const [boostDuration, setBoostDuration] = useState(defaultBoostDuration);
  return { showBoostPanel, setShowBoostPanel, boostBudget, setBoostBudget, boostDuration, setBoostDuration };
}

/**
 * A single portal publication row with status and actions. The post editor opens as a
 * dialog, or inline when the row is already inside another dialog (`compact`).
 */
export function PublicationRowItem(props: RowProps) {
  const {
    row, t, connectedPortals, removing, updating, editingRow, getStatusLabel,
    onEdit, onConfirmRemove, compact, boostingRow, onBoost,
    defaultBoostBudget = 0, defaultBoostDuration = 0, boostMissing, adSettingsHref,
    fbHasPaymentMethod = false, tokenExpiresAt, onRetry, retrying, onShowAdError, adAccountId, onEditProperty,
  } = props;
  const editsProperty = row.portal === 'olx';
  const boostState = useBoostPanelState(defaultBoostBudget, defaultBoostDuration);
  const isConnected = connectedPortals.includes(row.portal);
  const canEdit =
    EDITABLE_PORTALS.has(row.portal)
    && !isInstagramEditHidden(row.portal)
    && isConnected
    && Boolean(row.post_id || row.listing_id)
    && (!editsProperty || Boolean(onEditProperty));
  const canBoost = resolveCanBoost(row, isConnected, fbHasPaymentMethod, tokenExpiresAt);
  const isRemoving = removing === row.key;
  const isUpdating = updating === row.key;
  const isBoosting = boostingRow === row.key;
  const isRetrying = retrying === row.key;
  const postUrl = !compact ? getRowPostUrl(row) : null;

  return (
    <div className="rounded-(--radius-lg) p-(--space-3) border border-[var(--border-subtle)] space-y-(--space-1-5) bg-[var(--surface-primary)]" data-testid={`publication-row-${row.key}`}>
      <div className="flex flex-col gap-(--space-1-5) sm:flex-row sm:items-center sm:justify-between">
        <RowMeta
          row={row}
          compact={compact}
          getStatusLabel={getStatusLabel}
          onRetry={onRetry}
          retrying={retrying}
          onShowAdError={onShowAdError}
          adAccountId={adAccountId}
          t={t}
        />
        <RowActions
          row={row}
          canEdit={canEdit}
          canBoost={canBoost}
          compact={compact}
          isConnected={isConnected}
          isRemoving={isRemoving}
          isUpdating={isUpdating}
          isBoosting={isBoosting}
          isRetrying={isRetrying}
          showBoostPanel={boostState.showBoostPanel}
          onToggleBoost={() => boostState.setShowBoostPanel((v) => !v)}
          postUrl={postUrl}
          onEdit={editsProperty && onEditProperty ? () => onEditProperty() : onEdit}
          onConfirmRemove={onConfirmRemove}
          t={t}
        />
      </div>
      {!isConnected && <DisconnectedWarning portal={row.portal} t={t} />}
      <QueuedProcessingHint row={row} t={t} />
      {boostState.showBoostPanel && canBoost && !compact && (
        <BoostPanel
          rowKey={row.key}
          portal={row.portal}
          t={t}
          budget={boostState.boostBudget}
          duration={boostState.boostDuration}
          isBoosting={isBoosting}
          onBudget={boostState.setBoostBudget}
          onDuration={boostState.setBoostDuration}
          missing={boostMissing}
          settingsHref={adSettingsHref}
          onCancel={() => boostState.setShowBoostPanel(false)}
          onConfirm={() => {
            onBoost?.(row, boostState.boostBudget * 100, boostState.boostDuration);
            boostState.setShowBoostPanel(false);
          }}
        />
      )}
      {editingRow === row.key && (compact ? (
        <div className="space-y-(--space-1-5) pt-(--space-1)">
          <EditPanel {...props} />
        </div>
      ) : (
        <EditModal {...props} />
      ))}
    </div>
  );
}
